import { get } from "lodash-es";
import {
	MESApiService,
	type spParameter,
} from "../../../../services/ApiService";
import type { EmailAddress } from "../models/EmailAddress";
import type { EmailAddressTopic } from "../models/EmailAddressTopic";
import type { EmailTopic } from "../models/EmailTopic";

// GetEmailTopics answers with two result sets: the topics themselves and, per topic, one row per
// assigned address carrying its recipient type.
export const getEmailTopics = async ({
	emailTopicId = null,
	includeInactive = false,
	includeSystem = false,
}: {
	emailTopicId?: number | null;
	includeInactive?: boolean;
	includeSystem?: boolean;
} = {}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "EmailTopicId", value: emailTopicId },
		{ name: "IncludeInactive", value: includeInactive },
		{ name: "IncludeSystem", value: includeSystem },
	];

	const resp = await apiService.callV2("[SYSTEM].[GetEmailTopics]", parameters);

	if (resp.ok) {
		return {
			topics: get(resp, "data.tables[0].rows", []) as EmailTopic[],
			addresses: get(resp, "data.tables[1].rows", []) as EmailAddressTopic[],
		};
	}

	throw new Error(resp.message || "Error fetching email topics");
};

export const getEmailAddresses = async ({
	includeInactive = false,
	includeSystem = false,
}: {
	includeInactive?: boolean;
	includeSystem?: boolean;
} = {}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "IncludeInactive", value: includeInactive },
		{ name: "IncludeSystem", value: includeSystem },
	];

	const resp = await apiService.callV2(
		"[SYSTEM].[GetEmailAddresses]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as EmailAddress[];
	}

	throw new Error(resp.message || "Error fetching email addresses");
};

// Returns the id of the saved address, which the create flow needs to assign its topics.
export const upsertEmailAddress = async (address: {
	EmailAddressId: number | null;
	Address: string;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "EmailAddressId", value: address.EmailAddressId },
		{ name: "Address", value: address.Address.trim() },
	];

	const resp = await apiService.callV2(
		"[SYSTEM].[UpsertEmailAddress]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number | null;
	}

	throw new Error(resp.message || "Error saving email address");
};

// The three lists replace whatever the address had assigned: an id absent from all of them is
// unassigned. Sent as CSV because that is what the procedure parses.
export const setEmailAddressTopics = async (assignment: {
	EmailAddressId: number;
	TopicIdsTO: number[];
	TopicIdsCC: number[];
	TopicIdsBCC: number[];
	IncludeSystem: boolean;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "EmailAddressId", value: assignment.EmailAddressId },
		{ name: "TopicIdsTOCSV", value: assignment.TopicIdsTO.join(",") },
		{ name: "TopicIdsCCCSV", value: assignment.TopicIdsCC.join(",") },
		{ name: "TopicIdsBCCCSV", value: assignment.TopicIdsBCC.join(",") },
		{ name: "IncludeSystem", value: assignment.IncludeSystem },
	];

	const resp = await apiService.callV2(
		"[SYSTEM].[SetEmailAddressTopics]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error saving the address topics");
	}
};

export const deleteEmailAddress = async (emailAddressId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "EmailAddressId", value: emailAddressId },
	];

	const resp = await apiService.callV2(
		"[SYSTEM].[DeleteEmailAddress]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting email address");
	}
};

export type UpsertEmailTopicProps = {
	EmailTopicId: number | null;
	EmailTopicName: string;
	Description: string | null;
	DelayTimeMinutes: number | null;
	IsActive: boolean;
	AddressIdsTO: number[];
	AddressIdsCC: number[];
	AddressIdsBCC: number[];
};

export const upsertEmailTopic = async (topic: UpsertEmailTopicProps) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "EmailTopicId", value: topic.EmailTopicId },
		{ name: "EmailTopicName", value: topic.EmailTopicName.trim() },
		{ name: "Description", value: topic.Description },
		{ name: "DelayTimeMinutes", value: topic.DelayTimeMinutes },
		{ name: "IsActive", value: topic.IsActive },
		{ name: "AddressIdsTOCSV", value: topic.AddressIdsTO.join(",") },
		{ name: "AddressIdsCCCSV", value: topic.AddressIdsCC.join(",") },
		{ name: "AddressIdsBCCCSV", value: topic.AddressIdsBCC.join(",") },
	];

	const resp = await apiService.callV2(
		"[SYSTEM].[UpsertEmailTopic]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number | null;
	}

	throw new Error(resp.message || "Error saving email topic");
};

export const deleteEmailTopic = async (emailTopicId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "EmailTopicId", value: emailTopicId },
	];

	const resp = await apiService.callV2(
		"[SYSTEM].[DeleteEmailTopic]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting email topic");
	}
};

export const setEmailTopicEnable = async ({
	emailTopicId,
	enabled,
}: {
	emailTopicId: number;
	enabled: boolean;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "EmailTopicId", value: emailTopicId },
		{ name: "Enabled", value: enabled },
	];

	const resp = await apiService.callV2(
		"[SYSTEM].[SetEmailTopicEnable]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(
			resp.message ||
				`Error when ${enabled ? "enabling" : "disabling"} the email topic`,
		);
	}
};
